import { z } from 'zod';
import { anaestheticCaseRecordShape, caseRecordShape, communicationRecordShape, edtEntryRecordShape, procedureRecordShape, refineProcedure, } from './schemas.js';
/**
 * The wire contract for sync, used by the client to build requests and by the
 * server to validate them. One definition, two consumers — the same rule as
 * every other schema here.
 *
 * The only difference from the stored record types is dates: JSON has none, so
 * they travel as ISO strings and are coerced back on arrival. Everything else,
 * including which fields may be present on which procedure, is the same
 * validation the forms use. A record the client would refuse to save is a
 * record the server refuses to accept.
 */
/** Accepts a Date (same-process tests) or the ISO string that crosses the wire. */
const wireDate = z.union([z.date(), z.string().datetime({ offset: true })]).pipe(z.coerce.date());
/**
 * The dates every record carries. **Any date field added to a record type must
 * be added to a wire schema too**, or it arrives as a string, fails validation,
 * and — because one bad record fails the whole request — jams that device's
 * entire outbox until the field is covered.
 *
 * That is not hypothetical: `endsAt` was added to an EDT entry on 2026-08-31
 * and missed here, which stopped every entry on an affected device from
 * syncing until it was found the next day. `every date field survives the
 * wire` in sync.test.ts is the guard that now fails instead.
 */
const wireDates = {
    occurredAt: wireDate,
    createdAt: wireDate,
    updatedAt: wireDate,
    deletedAt: wireDate.optional(),
};
export const wireCaseSchema = caseRecordShape.extend(wireDates);
export const wireProcedureSchema = procedureRecordShape
    .extend(wireDates)
    .superRefine(refineProcedure);
export const wireCommunicationSchema = communicationRecordShape.extend(wireDates);
/** Plus `endsAt`, the optional end of a period — see the note on wireDates. */
export const wireEdtEntrySchema = edtEntryRecordShape.extend({
    ...wireDates,
    endsAt: wireDate.optional(),
});
export const wireAnaestheticCaseSchema = anaestheticCaseRecordShape.extend(wireDates);
export const pushRequestSchema = z.object({
    /**
     * Which device sent this. Recorded in logs only — never used for
     * authorisation, and never attached to a record.
     */
    deviceId: z.string().uuid(),
    /**
     * Cases travel ahead of procedures and the server writes them in that order,
     * so a procedure never arrives referencing a case that does not exist yet.
     */
    cases: z.array(wireCaseSchema).max(500),
    procedures: z.array(wireProcedureSchema).max(500),
    /*
     * Optional so a client running an older build — one that has not been
     * updated since EDT entries existed — still pushes successfully rather than
     * having every batch rejected as invalid.
     */
    edtEntries: z.array(wireEdtEntrySchema).max(500).default([]),
    anaestheticCases: z.array(wireAnaestheticCaseSchema).max(500).default([]),
    communications: z.array(wireCommunicationSchema).max(500).default([]),
});
/**
 * How far in the future a record's `updatedAt` may be before the server refuses
 * it. A device with a badly wrong clock would otherwise win every last-write
 * comparison forever, and its entries would sit at the top of the list for
 * years. See ARCHITECTURE.md.
 */
export const CLOCK_SKEW_TOLERANCE_MS = 24 * 60 * 60 * 1000;
export function isImplausiblyFuture(updatedAt, now = new Date()) {
    return updatedAt.getTime() > now.getTime() + CLOCK_SKEW_TOLERANCE_MS;
}
/** The largest batch one push carries. Matches the schema's cap. */
export const SYNC_BATCH_SIZE = 200;
//# sourceMappingURL=sync.js.map